import React, { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import { ColorPickerItem } from '@components/ColorPickerPopover/index.styles'
import CustomPopover from '@components/CustomPopover'
import useCustomTagData from '@modules/custom-tags/hooks/useCustomTagsData'
import { TagDropdownButton, TagNameWrapper, TagPickerWrapper } from '@modules/shortlist/index.styles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

export interface CustomTagCellProps {
  tagId?: number
  onTagUpdate: (newTagId?: number | null) => void
}

const CustomTagCell = ({ tagId: rawTagId, onTagUpdate }: CustomTagCellProps) => {
  const [localValue, setLocalValue] = useState<number | null>()
  const { tags: existingTags } = useCustomTagData()
  const { t } = useTranslation()

  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  const handleButtonClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setAnchor(e.currentTarget as HTMLDivElement)
  }

  const tagId = localValue || rawTagId

  const tag = useMemo(() => existingTags.find(existingTag => existingTag.id === tagId), [tagId, existingTags])

  const tagListItems = useMemo(() => {
    const existingTagItems = existingTags
      .sort((tagA, tagB) => tagA.order - tagB.order)
      .map(existingTag => (
        <TagPickerWrapper
          key={existingTag.id}
          onClick={() => {
            setAnchor(null)
            setLocalValue(existingTag.id)
            onTagUpdate(existingTag.id)
          }}
        >
          <ColorPickerItem $color={existingTag.color} $size="13px" />
          {existingTag.name}
        </TagPickerWrapper>
      ))

    return [
      <TagPickerWrapper
        key="not_set"
        onClick={() => {
          setAnchor(null)
          setLocalValue(null)
          onTagUpdate(null)
        }}
      >
        <ColorPickerItem $color="#EBEBEB" $size="13px" />
        {t(translationKeys.CUSTOM_LIST_CUSTOM_STATUS_NOT_SET)}
      </TagPickerWrapper>,
      ...existingTagItems,
    ]
  }, [existingTags])

  return (
    <>
      <TagDropdownButton
        onClick={handleButtonClick}
        disabled={existingTags.length === 0}
        backgroundColor={tag?.color || '#EBEBEB'}
        color={tag ? 'white' : 'black'}
        arrowColor={tag ? '#505050' : 'white'}
      >
        <TagNameWrapper>{tag?.name || t(translationKeys.CUSTOM_LIST_CUSTOM_STATUS_NOT_SET)}</TagNameWrapper>
        <KeyboardArrowDownIcon sx={{ ...(anchor && { transform: 'rotate(180deg)' }) }} />
      </TagDropdownButton>
      {existingTags.length ? (
        <CustomPopover
          id="custom-tag-selector"
          sx={{ marginTop: '10px', overflow: 'hidden', padding: '16px' }}
          anchor={anchor}
          setAnchor={setAnchor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          {tagListItems}
        </CustomPopover>
      ) : null}
    </>
  )
}

export default memo(CustomTagCell)
